import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { BRAND, LEGAL } from "../lib/brand";

const PAGES = {
  "mentions-legales": {
    title: "Mentions légales",
    intro: "Informations légales relatives à l'édition et à l'exploitation du site MAISON DOUBA, exploité par Wassa Shopping.",
    sections: [
      {
        h: "Éditeur du site",
        p: `Le site MAISON DOUBA (ci-après le « Site ») est édité par :\n\n${LEGAL.company_name} — ${LEGAL.legal_form}\nMarque commerciale : ${LEGAL.trademark}\nEntrepreneur : ${LEGAL.owner}\n\nSIREN : ${LEGAL.siren}\nSIRET : ${LEGAL.siret}\nCode APE : ${LEGAL.ape_code} (${LEGAL.ape_label})\n\nSiège social : ${LEGAL.address}\n\nContact : ${LEGAL.contact_email} — Téléphone : ${LEGAL.contact_phone}`,
      },
      {
        h: "Directeur de la publication",
        p: `Le directeur de la publication est ${LEGAL.owner}, entrepreneur individuel exploitant sous la dénomination ${LEGAL.company_name}. Toute correspondance relative à la publication du Site peut être adressée à l'adresse électronique indiquée ci-dessus.`,
      },
      {
        h: "Hébergement",
        p: "Le Site est hébergé sur une infrastructure cloud sécurisée conforme aux standards européens de protection des données. Les serveurs sont localisés dans l'Union européenne.",
      },
      {
        h: "Propriété intellectuelle",
        p: "L'ensemble des éléments présents sur le Site (textes, photographies, marques, logos, graphismes, mise en page) est protégé par le droit de la propriété intellectuelle. La marque « MAISON DOUBA » est exploitée par Wassa Shopping. Toute reproduction, représentation, modification ou exploitation partielle ou totale sans autorisation écrite préalable est strictement interdite.",
      },
      {
        h: "Responsabilité",
        p: `${LEGAL.company_name} met tout en œuvre pour assurer l'exactitude et la mise à jour des informations diffusées. Toutefois, elle ne saurait être tenue responsable des erreurs, omissions ou indisponibilités temporaires du Site.`,
      },
      {
        h: "Nous contacter",
        p: `Pour toute question relative aux présentes mentions légales, vous pouvez nous écrire à ${LEGAL.contact_email} ou nous joindre au ${LEGAL.contact_phone}.`,
      },
    ],
  },

  "cgv": {
    title: "Conditions générales de vente",
    intro: `Les présentes conditions régissent les ventes réalisées sur le site MAISON DOUBA, exploité par ${LEGAL.company_name} (${LEGAL.legal_form}), SIRET ${LEGAL.siret}.`,
    sections: [
      {
        h: "Article 1 — Identification du vendeur",
        p: `Le vendeur est ${LEGAL.company_name}, ${LEGAL.legal_form}, immatriculée sous le SIREN ${LEGAL.siren} et le SIRET ${LEGAL.siret}, code APE ${LEGAL.ape_code} (${LEGAL.ape_label}). Siège social : ${LEGAL.address}. Contact : ${LEGAL.contact_email} · ${LEGAL.contact_phone}.`,
      },
      {
        h: "Article 2 — Objet",
        p: "Les présentes CGV s'appliquent à toute commande passée sur le Site MAISON DOUBA. Elles définissent les droits et obligations des parties dans le cadre de la vente à distance de produits de parfumerie et d'horlogerie.",
      },
      {
        h: "Article 3 — Produits",
        p: "Les produits proposés sont ceux figurant sur le Site au jour de la commande, dans la limite des stocks disponibles. Wassa Shopping se réserve le droit de retirer un produit du Site à tout moment.",
      },
      {
        h: "Article 4 — Prix",
        p: "Les prix sont indiqués en euros, toutes taxes comprises. Les frais de livraison sont indiqués avant validation de la commande. La livraison est offerte dès 60€ d'achat en France métropolitaine et dès 160€ d'achat à l'international.",
      },
      {
        h: "Article 5 — Commande",
        p: "Toute commande vaut acceptation des présentes CGV. Après validation du paiement, un email de confirmation récapitulant la commande est envoyé à l'adresse indiquée par le client.",
      },
      {
        h: "Article 6 — Paiement",
        p: "Le paiement s'effectue en ligne par carte bancaire (Visa, Mastercard, American Express), Apple Pay ou PayPal. Les transactions sont chiffrées et sécurisées via un tiers de confiance. Aucune donnée bancaire n'est stockée sur les serveurs de Wassa Shopping.",
      },
      {
        h: "Article 7 — Livraison",
        p: "Les commandes sont expédiées sous 48 à 72h ouvrées depuis notre entrepôt situé à Le Plessis-Robinson. Les délais indicatifs de livraison sont de 48 à 72h en France métropolitaine, 3 à 5 jours ouvrés à l'international. Un lien de suivi est adressé par email dès l'expédition.",
      },
      {
        h: "Article 8 — Droit de rétractation",
        p: "Conformément aux articles L.221-18 et suivants du Code de la consommation, le client dispose d'un délai de 14 jours à compter de la réception pour retourner tout produit dans son emballage d'origine, non ouvert et non descellé. Voir notre politique de retour pour les modalités.",
      },
      {
        h: "Article 9 — Garantie",
        p: `Les produits bénéficient de la garantie légale de conformité et de la garantie contre les vices cachés. En cas de défaut, le client peut nous contacter à ${LEGAL.contact_email} pour obtenir un remboursement ou un remplacement.`,
      },
      {
        h: "Article 10 — Médiation & droit applicable",
        p: "Les présentes CGV sont régies par le droit français. En cas de litige, le client peut recourir à un médiateur de la consommation avant toute action judiciaire. Tout litige relatif à leur interprétation ou exécution relève de la compétence des tribunaux français.",
      },
    ],
  },

  "politique-confidentialite": {
    title: "Politique de confidentialité",
    intro: `${LEGAL.company_name} (exploitant la marque ${LEGAL.trademark}) s'engage à protéger la vie privée et les données personnelles de ses visiteurs et clients, conformément au RGPD.`,
    sections: [
      {
        h: "Responsable du traitement",
        p: `Le responsable du traitement des données est ${LEGAL.company_name}, ${LEGAL.legal_form}, SIRET ${LEGAL.siret}, dont le siège social est situé au ${LEGAL.address}. Contact DPO : ${LEGAL.contact_email}.`,
      },
      {
        h: "Données collectées",
        p: "Nous collectons uniquement les données nécessaires au traitement de vos commandes et à la relation client : nom, prénom, adresse email, adresse postale, numéro de téléphone, historique de commandes. Aucune donnée bancaire n'est stockée sur nos serveurs.",
      },
      {
        h: "Finalité",
        p: "Vos données sont utilisées exclusivement pour :\n• le traitement et l'expédition de vos commandes ;\n• la communication liée à votre commande ;\n• l'envoi de notre newsletter (uniquement en cas d'inscription volontaire) ;\n• le respect de nos obligations légales et comptables.",
      },
      {
        h: "Base légale",
        p: "Le traitement de vos données est fondé sur l'exécution du contrat de vente (commande) et sur votre consentement (newsletter). Vos données ne sont jamais vendues ni cédées à des tiers à des fins commerciales.",
      },
      {
        h: "Durée de conservation",
        p: "Les données de compte et de commande sont conservées pendant la durée nécessaire à la relation commerciale, puis archivées conformément aux obligations légales (10 ans pour les factures).",
      },
      {
        h: "Cookies",
        p: "Le Site utilise des cookies techniques strictement nécessaires à son fonctionnement (panier, session). Des cookies de mesure d'audience peuvent être déposés avec votre consentement.",
      },
      {
        h: "Vos droits (RGPD)",
        p: `Conformément au Règlement Général sur la Protection des Données, vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition sur vos données. Pour l'exercer, écrivez-nous à ${LEGAL.contact_email}. Vous pouvez également introduire une réclamation auprès de la CNIL.`,
      },
      {
        h: "Contact",
        p: `Pour toute question relative à la protection de vos données : ${LEGAL.contact_email} — ${LEGAL.contact_phone}.`,
      },
    ],
  },

  "politique-retour": {
    title: "Politique de retour",
    intro: "Chez MAISON DOUBA, votre satisfaction est notre priorité. Nous acceptons les retours sous 14 jours conformément à la législation française.",
    sections: [
      {
        h: "Délai de rétractation",
        p: "Conformément aux articles L.221-18 et suivants du Code de la consommation, vous disposez d'un délai de 14 jours à compter de la réception de votre commande pour nous retourner tout produit qui ne vous conviendrait pas.",
      },
      {
        h: "Conditions d'éligibilité",
        p: "Pour être éligible à un remboursement, le produit doit être :\n• dans son état d'origine, non descellé et non ouvert ;\n• accompagné de son emballage complet (écrin, notice) ;\n• accompagné du bon de retour fourni par nos soins.\n\nLes produits utilisés, endommagés ou sans emballage d'origine ne pourront être repris.",
      },
      {
        h: "Comment initier un retour",
        p: `1. Contactez-nous à ${LEGAL.contact_email} ou au ${LEGAL.contact_phone} en précisant votre numéro de commande.\n\n2. Vous recevrez sous 24h un bon de retour prépayé par email.\n\n3. Emballez soigneusement le produit dans son emballage d'origine.\n\n4. Déposez le colis dans un point relais indiqué sur le bon.`,
      },
      {
        h: "Frais de retour",
        p: "Les frais de retour sont intégralement pris en charge par MAISON DOUBA pour toute commande passée en France métropolitaine. À l'international, les frais restent à la charge du client sauf en cas de produit défectueux ou non conforme.",
      },
      {
        h: "Remboursement",
        p: "Une fois le produit reçu et vérifié dans nos ateliers, le remboursement est effectué sous 5 à 10 jours ouvrés sur le moyen de paiement d'origine. Un email de confirmation vous est adressé dès l'émission du remboursement.",
      },
      {
        h: "Échange",
        p: "Vous pouvez également demander un échange contre un autre produit de valeur équivalente ou supérieure (avec complément de prix). L'échange suit les mêmes délais que le remboursement.",
      },
      {
        h: "Produit défectueux",
        p: `Si vous recevez un produit endommagé ou non conforme, contactez-nous immédiatement à ${LEGAL.contact_email}. Un remplacement ou un remboursement intégral sera effectué sans frais supplémentaires.`,
      },
    ],
  },
};

const LegalPage = () => {
  const { slug } = useParams();
  const page = PAGES[slug];

  if (!page) return <Navigate to="/" replace />;

  return (
    <div data-testid={`legal-page-${slug}`} className="bg-[#050505] min-h-screen">
      <Navbar />

      <div className="pt-32 pb-24 max-w-[900px] mx-auto px-6 md:px-12">
        <Link to="/" data-testid="legal-back" className="label-caps text-white/50 hover:text-[color:var(--gold)] inline-flex items-center gap-3 mb-14">
          <ArrowLeft size={14} /> Retour
        </Link>

        <p className="label-caps text-[color:var(--gold)] mb-6">Informations légales</p>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-8">{page.title}</h1>
        <p className="text-white/60 font-light text-lg leading-relaxed mb-16 max-w-2xl">{page.intro}</p>

        <div className="hairline-gold opacity-40 mb-16" />

        <div className="space-y-14">
          {page.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-serif text-2xl md:text-3xl text-[color:var(--gold-soft)] mb-6">{s.h}</h2>
              <p className="text-white/70 font-light leading-relaxed whitespace-pre-line">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="hairline-gold opacity-40 mt-20 mb-10" />
        <div className="text-white/50 text-sm font-light space-y-1">
          <p>
            Éditeur : <span className="text-white/70">{LEGAL.company_name}</span> · {LEGAL.legal_form} · {LEGAL.owner}
          </p>
          <p>
            SIREN {LEGAL.siren} · SIRET {LEGAL.siret} · Code APE {LEGAL.ape_code}
          </p>
          <p>
            {LEGAL.address}
          </p>
          <p className="mt-4">
            Dernière mise à jour : février 2026. Pour toute question, écrivez-nous à <a href={`mailto:${BRAND.email}`} className="text-[color:var(--gold)] hover:underline">{BRAND.email}</a> ou appelez le <a href={`tel:${BRAND.phone_link}`} className="text-[color:var(--gold)] hover:underline">{BRAND.phone_display}</a>.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LegalPage;
